import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Bike, Check, AlertCircle,
  User, Gauge, Instagram, Loader2, Upload, X, Home,
} from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import ProblemSolution from "@/components/landing/ProblemSolution";
import HowItWorks from "@/components/landing/HowItWorks";
import ScrollProgress from "@/components/landing/ScrollProgress";
import { INDIAN_STATES, BIKE_BRANDS, citiesForState } from "@/data/formOptions";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const INSTAGRAM_URL = "https://www.instagram.com/bikerboulevard";

const INITIAL = {
  full_name: "", phone: "", whatsapp: "", email: "",
  city: "", state: "",
  brand: "", model: "", variant: "",
  registration_number: "", registration_state: "", registration_city: "", registration_year: "",
  kms_driven: "", fuel_type: "Petrol", previous_owners: "1", current_location: "",
  insurance_status: "", insurance_expiry: "",
  puc_status: "", service_history: "",
  condition: "", condition_notes: "",
  has_loan: "No", loan_amount: "",
  has_challan: "No", challan_amount: "",
  registered_owner: "Yes", documents_available: "Yes",
  expected_price: "", reason_for_selling: "", additional_notes: "",
};

const YEARS = (() => {
  const current = new Date().getFullYear();
  const arr = [];
  for (let y = current; y >= 2012; y--) arr.push(String(y));
  return arr;
})();

// =========================
// Reusable field components
// =========================
function FieldLabel({ children, required }) {
  return (
    <label className="block text-[13px] font-semibold text-zinc-300 mb-2 font-sans">
      {children}
      {required && <span className="text-[#00cccc] ml-1">*</span>}
    </label>
  );
}

const INPUT_BASE = "w-full px-4 py-3.5 text-[15px] bg-[#101317] border rounded-xl text-white placeholder:text-zinc-500 font-sans focus:outline-none focus:ring-2 focus:ring-[#00cccc]/50 focus:border-[#00cccc]/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed";

function TextInput({ label, name, value, onChange, placeholder = "", type = "text", required, error, disabled, testid, maxLength, autoComplete }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        autoComplete={autoComplete}
        data-testid={testid || `input-${name}`}
        className={`${INPUT_BASE} ${error ? "border-red-500/60 focus:ring-red-500/40" : "border-white/10 hover:border-white/20"}`}
      />
      {error && <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
    </div>
  );
}

function SelectInput({ label, name, value, onChange, options, required, error, testid, placeholder = "Select...", disabled }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <select
        value={value || ""}
        onChange={(e) => onChange(name, e.target.value)}
        disabled={disabled}
        data-testid={testid || `select-${name}`}
        className={`${INPUT_BASE} ${error ? "border-red-500/60 focus:ring-red-500/40" : "border-white/10 hover:border-white/20"}`}
        style={{ colorScheme: "dark" }}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map((o) => (
          typeof o === "object"
            ? <option key={o.value} value={o.value} className="bg-[#15181C] text-white">{o.label}</option>
            : <option key={o} value={o} className="bg-[#15181C] text-white">{o}</option>
        ))}
      </select>
      {error && <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
    </div>
  );
}

function RadioGroup({ label, name, value, onChange, options, required, testid }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value === o;
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(name, o)}
              data-testid={testid ? `${testid}-${o.toLowerCase().replace(/\s+/g, "-")}` : `radio-${name}-${o.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-5 py-2.5 rounded-xl text-[14px] font-medium border transition-all
                ${active
                  ? "bg-[#00cccc]/15 border-[#00cccc] text-[#00cccc] shadow-[0_0_16px_rgba(0,204,204,0.25)]"
                  : "bg-[#15181C] border-white/10 text-zinc-300 hover:border-white/20 hover:text-white"}`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TextArea({ label, name, value, onChange, placeholder, rows = 4, error, testid, maxLength }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(name, e.target.value)}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        data-testid={testid || `textarea-${name}`}
        className={`${INPUT_BASE} resize-none ${error ? "border-red-500/60" : ""}`}
      />
    </div>
  );
}

function SectionHeader({ n, icon: Icon, title, subtitle }) {
  return (
    <div className="mb-6 flex items-start gap-4">
      <div className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center bg-[#00cccc]/10 border border-[#00cccc]/30">
        <Icon className="w-5 h-5 text-[#00cccc]" />
      </div>
      <div>
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Section {n}</div>
        <h2 className="mt-1 text-xl sm:text-2xl font-bold tracking-tight text-white font-sans">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-zinc-400">{subtitle}</p>}
      </div>
    </div>
  );
}

// =========================
// Validation
// =========================
function validateAll(f) {
  const e = {};
  if (!f.full_name?.trim() || f.full_name.trim().length < 2) e.full_name = "Enter your full name";
  if (!/^(\+91)?[6-9]\d{9}$/.test((f.phone || "").replace(/\s+/g, ""))) e.phone = "Enter a valid 10-digit Indian mobile";
  if (!/^(\+91)?[6-9]\d{9}$/.test((f.whatsapp || "").replace(/\s+/g, ""))) e.whatsapp = "Enter a valid 10-digit Indian mobile";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email || "")) e.email = "Enter a valid email";
  if (!f.city?.trim()) e.city = "Select your city";
  if (!f.state) e.state = "Select your state";
  if (!f.brand) e.brand = "Select brand";
  if (!f.model?.trim()) e.model = "Enter model";
  if (!f.registration_number?.trim()) e.registration_number = "Enter registration number";
  if (!f.registration_state) e.registration_state = "Select registration state";
  if (!f.registration_city) e.registration_city = "Select registration city";
  if (!f.registration_year) e.registration_year = "Select year";
  if (!/^\d+$/.test((f.kms_driven || "").trim())) e.kms_driven = "Enter KMs driven";
  if (!/^\d+$/.test((f.expected_price || "").trim())) e.expected_price = "Enter expected price (numbers only)";
  return e;
}

// =========================
// Main page
// =========================
export default function SellMyBike() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [whatsappSameAsPhone, setWhatsappSameAsPhone] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [catalog, setCatalog] = useState({}); // brand -> [models]
  const [modelOther, setModelOther] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    document.title = "Sell My Bike — Biker Boulevard";
    window.scrollTo(0, 0);
  }, []);

  // Brand -> model catalog for the model dropdown
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/valuation/catalog`)
      .then((r) => r.json())
      .then((d) => setCatalog(d.brands || {}))
      .catch(() => {});
  }, []);

  const onChange = (name, value) => {
    if (name === "brand") setModelOther(false);
    if (name === "model" && value === "__other__") { setModelOther(true); value = ""; }
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === "registration_number") next.registration_number = (value || "").toUpperCase();
      if (name === "registration_state") next.registration_city = "";
      if (name === "state") next.city = "";
      if (name === "brand") next.model = "";
      if (name === "phone" && whatsappSameAsPhone) next.whatsapp = value;
      return next;
    });
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const toggleWhatsappSame = (checked) => {
    setWhatsappSameAsPhone(checked);
    if (checked) setForm((f) => ({ ...f, whatsapp: f.phone }));
  };

  const onPhotos = (files) => {
    const arr = Array.from(files || []).slice(0, 8);
    setPhotos(arr);
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async () => {
    const v = validateAll(form);
    setErrors(v);
    if (Object.keys(v).length > 0) {
      // scroll to first error
      requestAnimationFrame(() => {
        const first = document.querySelector('[class*="border-red-500"]');
        if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        ...form,
        photo_file_names: photos.map((p) => p.name),
      };
      const res = await fetch(`${API_URL}/api/leads/sell-bike`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Something went wrong. Please try again.");
      }
      await res.json();
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="sell-my-bike-page" className="relative bg-[#0D0F12] text-white min-h-screen overflow-x-hidden">
      <ScrollProgress />
      <Navbar />

      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 10%, rgba(0,204,204,0.08), transparent 60%), radial-gradient(ellipse at 80% 90%, rgba(0,204,204,0.05), transparent 60%), #0D0F12" }} />
        <div className="absolute inset-0 grid-bg opacity-10" />
      </div>

      <div className="pt-24 pb-24 max-w-[1000px] mx-auto px-5 md:px-8">
        {!submitted && (
          <>
            {/* Compact header */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00cccc]/25 bg-[#00cccc]/5 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00cccc] animate-pulse-glow" />
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#00cccc] font-mono">// Sell My Bike</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05] tracking-tight">
                Sell directly to <span className="text-gradient-cyan">Biker Boulevard</span> for best value of your bike
              </h1>
              <p className="mt-3 text-zinc-400 text-base sm:text-lg max-w-2xl">
                Give us a few details about your motorcycle and get one step closer to selling it through Biker Boulevard. Our Smart Valuation Engine analyzes your bike's brand, model, year, mileage and condition to prepare a fair, data-driven offer — no haggling with dealers, no strangers at your door. Share your details below and our team will reach out to schedule a quick inspection and complete the purchase.
              </p>
            </motion.div>

            {/* FORM — charcoal grey container */}
            <motion.div
              ref={formRef}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-2xl p-5 sm:p-8 space-y-10 border border-white/10"
              style={{ background: "#15181C" }}
            >
              {/* SECTION 1 */}
              <section data-testid="section-customer">
                <SectionHeader n="01" icon={User} title="About You" />
                <div className="space-y-5">
                  <TextInput label="Full Name" name="full_name" value={form.full_name} onChange={onChange} required error={errors.full_name} placeholder="e.g. Rohit Sharma" autoComplete="name" />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextInput label="Phone Number" name="phone" value={form.phone} onChange={onChange} required error={errors.phone} placeholder="10-digit mobile" type="tel" autoComplete="tel" maxLength={13} />
                    <TextInput label="WhatsApp Number" name="whatsapp" value={form.whatsapp} onChange={onChange} required error={errors.whatsapp} placeholder="10-digit mobile" type="tel" disabled={whatsappSameAsPhone} maxLength={13} />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-zinc-300 hover:text-white transition-colors" data-testid="whatsapp-same-toggle">
                    <input
                      type="checkbox"
                      checked={whatsappSameAsPhone}
                      onChange={(e) => toggleWhatsappSame(e.target.checked)}
                      className="w-4 h-4 rounded accent-[#00cccc]"
                    />
                    WhatsApp number is the same as my phone number
                  </label>
                  <TextInput label="Email Address" name="email" value={form.email} onChange={onChange} required error={errors.email} placeholder="you@example.com" type="email" autoComplete="email" />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <SelectInput label="State" name="state" value={form.state} onChange={onChange} options={INDIAN_STATES} required error={errors.state} placeholder="Select state" />
                    <SelectInput label="City" name="city" value={form.city} onChange={onChange} options={citiesForState(form.state)} required error={errors.city} disabled={!form.state} placeholder={form.state ? "Select city" : "Select state first"} />
                  </div>
                </div>
              </section>

              {/* Divider */}
              <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              {/* SECTION 2 */}
              <section data-testid="section-motorcycle">
                <SectionHeader n="02" icon={Bike} title="Tell Us About Your Bike" />
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <SelectInput label="Brand" name="brand" value={form.brand} onChange={onChange} options={BIKE_BRANDS} required error={errors.brand} placeholder="Select brand" />
                    {catalog[form.brand] && !modelOther ? (
                      <SelectInput label="Model" name="model" value={form.model} onChange={onChange}
                        options={[...catalog[form.brand], { value: "__other__", label: "Other (type manually)" }]}
                        required error={errors.model} placeholder="Select model" />
                    ) : (
                      <div>
                        <TextInput label="Model" name="model" value={form.model} onChange={onChange} required error={errors.model} placeholder="e.g. Duke 390" />
                        {catalog[form.brand] && (
                          <button type="button" onClick={() => setModelOther(false)} className="mt-1 text-[11px] text-[#00cccc] hover:underline" data-testid="model-list-link">
                            Choose from {form.brand} models
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <TextInput label="Variant (if applicable)" name="variant" value={form.variant} onChange={onChange} placeholder="e.g. Standard / STD / Pro" />
                  <div className="grid sm:grid-cols-3 gap-5">
                    <TextInput label="Registration Number" name="registration_number" value={form.registration_number} onChange={onChange} required error={errors.registration_number} placeholder="e.g. UP32 AB 1234" />
                    <SelectInput label="Registration State" name="registration_state" value={form.registration_state} onChange={onChange} options={INDIAN_STATES} required error={errors.registration_state} placeholder="Select state" />
                    <SelectInput label="Registration City" name="registration_city" value={form.registration_city} onChange={onChange} options={citiesForState(form.registration_state)} required error={errors.registration_city} disabled={!form.registration_state} placeholder={form.registration_state ? "Select city" : "Select state first"} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <SelectInput label="Year of Registration" name="registration_year" value={form.registration_year} onChange={onChange} options={YEARS} required error={errors.registration_year} placeholder="Select year" />
                    <TextInput label="KMs Driven" name="kms_driven" value={form.kms_driven} onChange={onChange} required error={errors.kms_driven} placeholder="e.g. 12000" type="number" />
                  </div>
                  <RadioGroup label="Fuel Type" name="fuel_type" value={form.fuel_type} onChange={onChange} options={["Petrol", "Electric", "Other"]} required />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <SelectInput label="Number of Previous Owners" name="previous_owners" value={form.previous_owners} onChange={onChange} options={["1 (First)", "2", "3", "4+"]} required />
                    <TextInput label="Current Location" name="current_location" value={form.current_location} onChange={onChange} placeholder="City / State (optional)" />
                  </div>
                </div>
              </section>

              {/* Divider */}
              <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

              {/* SECTION 3 */}
              <section data-testid="section-details">
                <SectionHeader n="03" icon={Gauge} title="A Few More Details" />
                <div className="space-y-8">
                  <div className="space-y-5">
                    <RadioGroup label="Insurance Status" name="insurance_status" value={form.insurance_status} onChange={onChange} options={["Valid", "Expired", "Don't Know"]} />
                    {form.insurance_status === "Valid" && (
                      <TextInput label="Insurance Expiry Date" name="insurance_expiry" value={form.insurance_expiry} onChange={onChange} placeholder="DD/MM/YYYY" />
                    )}
                    <RadioGroup label="PUC Status" name="puc_status" value={form.puc_status} onChange={onChange} options={["Valid", "Expired", "Don't Know"]} />
                    <RadioGroup label="Service History" name="service_history" value={form.service_history} onChange={onChange} options={["Full", "Partial", "None", "Don't Know"]} />
                    <RadioGroup label="Motorcycle Condition" name="condition" value={form.condition} onChange={onChange} options={["Showroom Condition", "Very Good", "Good", "Average", "Needs Work"]} />
                    <TextArea label="Condition Notes (optional)" name="condition_notes" value={form.condition_notes} onChange={onChange} placeholder="Any scratches, dents, or specific issues" rows={3} maxLength={1000} />
                  </div>

                  <div className="pt-6 border-t border-white/10 space-y-5">
                    <RadioGroup label="Active loan on the motorcycle?" name="has_loan" value={form.has_loan} onChange={onChange} options={["No", "Yes"]} />
                    {form.has_loan === "Yes" && (
                      <TextInput label="Approximate Outstanding Loan Amount (₹)" name="loan_amount" value={form.loan_amount} onChange={onChange} placeholder="e.g. 80000" type="number" />
                    )}
                    <RadioGroup label="Pending challans?" name="has_challan" value={form.has_challan} onChange={onChange} options={["No", "Yes", "Don't Know"]} />
                    {form.has_challan === "Yes" && (
                      <TextInput label="Approximate Pending Challan Amount (₹)" name="challan_amount" value={form.challan_amount} onChange={onChange} placeholder="e.g. 1500" type="number" />
                    )}
                    <RadioGroup label="Motorcycle currently registered in your name?" name="registered_owner" value={form.registered_owner} onChange={onChange} options={["Yes", "No"]} />
                    <RadioGroup label="Are all original documents available?" name="documents_available" value={form.documents_available} onChange={onChange} options={["Yes", "No", "Some are missing"]} />
                  </div>

                  <div className="pt-6 border-t border-white/10 space-y-5">
                    <div>
                      <TextInput label="Expected Price (₹)" name="expected_price" value={form.expected_price} onChange={onChange} required error={errors.expected_price} placeholder="e.g. 280000" type="number" />
                      <p className="mt-2 text-xs text-zinc-500 leading-relaxed">Your expected price helps us understand your expectations. Our final buying price will be determined after valuation and inspection.</p>
                    </div>
                    <SelectInput label="Why are you selling your bike?" name="reason_for_selling" value={form.reason_for_selling} onChange={onChange} options={["Upgrading", "Moving to another city", "Not using it enough", "Financial requirement", "Buying another motorcycle", "Other"]} placeholder="Select reason (optional)" />
                    <TextArea label="Anything else we should know? (optional)" name="additional_notes" value={form.additional_notes} onChange={onChange} placeholder="Accident history, modifications, scratches, mechanical issues, accessories, recent repairs, etc." rows={4} maxLength={2000} />
                  </div>

                  <div className="pt-6 border-t border-white/10">
                    <div className="mb-4">
                      <div className="text-lg font-semibold text-white">Want a Faster Assessment?</div>
                      <p className="mt-1 text-sm text-zinc-400">Attach a few photos of your motorcycle (front, rear, sides, odometer, RC, any visible damage). Photo upload is optional.</p>
                    </div>
                    <label
                      htmlFor="photo-input"
                      data-testid="photo-dropzone"
                      className="block cursor-pointer rounded-2xl border border-dashed border-[#00cccc]/30 bg-[#00cccc]/[0.03] hover:bg-[#00cccc]/[0.06] hover:border-[#00cccc]/60 transition-all p-8 text-center"
                    >
                      <Upload className="w-6 h-6 text-[#00cccc] mx-auto mb-2" />
                      <div className="text-sm text-white font-semibold">Drop photos or click to upload</div>
                      <div className="text-xs text-zinc-400 mt-1">JPG / PNG · Up to 8 files</div>
                      <input
                        id="photo-input"
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => onPhotos(e.target.files)}
                        className="hidden"
                        data-testid="photo-input"
                      />
                    </label>
                    {photos.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {photos.map((p, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-xs">
                            <span className="text-zinc-300 max-w-[180px] truncate">{p.name}</span>
                            <button onClick={() => removePhoto(i)} className="text-zinc-400 hover:text-red-400" data-testid={`remove-photo-${i}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Submit */}
              <div className="pt-4 space-y-4">
                {submitError && (
                  <div className="p-4 rounded-xl border border-red-500/40 bg-red-500/10 text-sm text-red-300 flex items-start gap-2" data-testid="submit-error">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{submitError}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="submit-button"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#00cccc] text-black font-semibold text-[15px] hover:shadow-[0_0_28px_rgba(0,204,204,0.6)] transition-all disabled:opacity-70"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit & Get My Bike Valued <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-xs text-zinc-500">Your details are secure. We&apos;ll only contact you about your bike.</p>
              </div>
            </motion.div>
          </>
        )}

        {submitted && <SuccessScreen navigate={navigate} />}
      </div>

      <HowItWorks />
      <ProblemSolution />
      <Footer />
    </main>
  );
}

function SuccessScreen({ navigate }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-[70vh] flex flex-col items-center justify-center text-center"
      data-testid="success-screen"
    >
      <motion.div
        initial={{ scale: 0, rotate: -90 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", damping: 12, stiffness: 200 }}
        className="relative w-28 h-28 mb-8"
      >
        <div className="absolute inset-0 rounded-full bg-[#00cccc]/10 border border-[#00cccc]/30 animate-pulse-glow" style={{ boxShadow: "0 0 48px rgba(0,204,204,0.35)" }} />
        <div className="absolute inset-3 rounded-full bg-[#00cccc]/20 border border-[#00cccc]/60 flex items-center justify-center">
          <Check className="w-12 h-12 text-[#00cccc]" strokeWidth={3} />
        </div>
      </motion.div>

      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
        You&apos;re In. <span className="text-gradient-cyan">We&apos;ll Take It From Here.</span>
      </h2>
      <p className="mt-4 text-zinc-400 max-w-xl">
        Thanks for choosing Biker Boulevard. We&apos;ve received your motorcycle details and our team will review them shortly.
      </p>
      <p className="mt-3 text-sm text-[#00cccc] font-mono">
        No more waiting. Your motorcycle journey starts here.
      </p>

      <div className="mt-10 p-6 rounded-2xl glass max-w-lg w-full">
        <div className="flex items-center justify-center gap-2 text-[#00cccc] mb-3">
          <Instagram className="w-5 h-5" />
          <span className="text-[10px] font-mono tracking-[0.3em] uppercase">// Follow Biker Boulevard</span>
        </div>
        <p className="text-sm text-zinc-400">
          Follow us for new motorcycle arrivals, rider stories, exclusive drops, events and Biker Boulevard updates.
        </p>
        <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="success-instagram"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#00cccc] text-black font-semibold hover:shadow-[0_0_28px_rgba(0,204,204,0.6)] transition-all"
          >
            <Instagram className="w-4 h-4" />
            Follow Us on Instagram
          </a>
          <Link
            to="/"
            data-testid="success-home"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white hover:border-[#00cccc]/40 hover:text-[#00cccc] transition-all"
          >
            <Home className="w-4 h-4" />
            Back to Biker Boulevard
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
